import { Action, Module, Mutation, VuexModule, getModule } from 'vuex-module-decorators';
import Remote from '@/api/route';
import store from '@/store/index';
import date from '@/common/utils/date';
import { QueryParameters, Route, TrackableAssetStateChangedMessage } from '@/api/types';
import { SearchFilters } from '@/types/search';

function initialState() {
  return {
    routes: [],
    selectedRouteTrackingData: null
  };
}

@Module({
  namespaced: true,
  name: 'routeMod',
  dynamic: true,
  store
})
export default class RouteModule extends VuexModule {
  routes: Route[] = initialState().routes;
  selectedRouteTrackingData: Route | null = initialState().selectedRouteTrackingData;

  get getRoutes(): Route[] {
    return this.routes;
  }

  get getSelectedRouteTrackingData(): Route | null {
    return this.selectedRouteTrackingData;
  }

  @Mutation
  private SET_SELECTED_ROUTE_TRACKING_DATA(route: Route | null): void {
    this.selectedRouteTrackingData = route;
  }

  @Mutation
  private ADD_ROUTE(route: Route): void {
    this.routes.push(route);
  }

  @Mutation
  private SET_ROUTES(routes: Route[]): void {
    this.routes = routes;
  }

  @Mutation
  private SET_ROUTE(route: Route): void {
    const index = this.routes.findIndex((x) => x.deliveryRunReference === route.deliveryRunReference);

    this.routes[index] = route;
  }

  @Mutation
  public RESET_ROUTES(): void {
    this.routes = initialState().routes;
    this.selectedRouteTrackingData = initialState().selectedRouteTrackingData;
  }

  @Action
  public async fetchRoutes(searchFilters: SearchFilters): Promise<void> {
    const routeQueryParameters: QueryParameters = {
      startDateTimeUtc: date.startOfDay(new Date(searchFilters.dateFrom)),
      endDateTimeUtc: date.endOfDay(new Date(searchFilters.dateTo)),
      searchTerm: searchFilters.searchText
    };

    const routes = await Remote.GetRoutes(routeQueryParameters);
    this.SET_ROUTES(routes);
  }

  @Action
  public async fetchRoute(reference: string): Promise<void> {
    const route = await Remote.GetRoute(reference);
    if (!!route && !this.routes.find((item) => item.deliveryRunReference === route?.deliveryRunReference)) {
      this.ADD_ROUTE(route);
    }
  }

  @Action
  public setSelectedRouteTrackingData(reference: string | null): void {
    const route = this.routes.find((item) => item.deliveryRunReference === reference);

    if (reference && route) {
      this.SET_SELECTED_ROUTE_TRACKING_DATA(route);
    } else {
      this.SET_SELECTED_ROUTE_TRACKING_DATA(null);
    }
  }

  @Action
  public updateRouteTrackableAssetStates(trackableAssetStateChangedMessage: TrackableAssetStateChangedMessage): void {
    const route = this.routes.find(
      (route) => route.trackable.trackingDeviceIdentifier === trackableAssetStateChangedMessage.trackingDeviceIdentifier
    );

    if (!route) return;

    route.travelledPath = route.travelledPath.concat(trackableAssetStateChangedMessage.trackableAssetStates);

    this.SET_ROUTE(route);
  }
}

export const routeMod = getModule(RouteModule);
